import type { TFunction } from 'i18next';
import { BookOpen, Bookmark, Building2, Euro, Home, LucideIcon, MapPinHouse, RefreshCw, Scale, ScrollText, TrendingDown, TrendingUp, UserMinus, UserPlus } from 'lucide-react';
import type { CompanyHistory, CompanyHistoryMember, InsolvencyRecordItem, RegisterFieldEventData } from '@/lib/backend-api';
import { foundingDateAtPrecision } from '@/lib/company-utils';

// =============================================================================
// TYPES
// =============================================================================

export interface CompanyEvent {
  timestamp: number;
  date: string;
  icon: LucideIcon;
  iconBg: string;
  iconColor: string;
  title: string;
  value?: string;
  detail?: string;
  alwaysExpanded?: boolean;
}

export interface CapitalEvent {
  timestamp: number;
  date: string;
  icon: LucideIcon;
  iconBg: string;
  iconColor: string;
  type: string;
  delta?: string;
  deltaColor?: string;
  total: string;
}

export interface BoardEvent {
  timestamp: number;
  date: string;
  icon: LucideIcon;
  iconBg: string;
  iconColor: string;
  name: string;
  role?: string;
  age?: number | null;
  status: string;
  statusColor?: string;
}

export interface InsolvencyEvent {
  timestamp: number;
  date: string;
  category: string;
  aktenzeichen: string;
  court: string;
  dissolved: boolean;
}

// =============================================================================
// HELPERS
// =============================================================================

function parseDDMMYYYY(
  dateStr?: string,
): { year: number; month: number; day: number } | null {
  if (!dateStr) return null;
  const parts = dateStr.split('.');
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    if (!isNaN(day) && !isNaN(month) && !isNaN(year))
      return { year, month, day };
  }
  const iso = new Date(dateStr);
  if (!isNaN(iso.getTime())) {
    return {
      year: iso.getFullYear(),
      month: iso.getMonth() + 1,
      day: iso.getDate(),
    };
  }
  return null;
}

function toTimestamp(dateStr?: string): number | null {
  const p = parseDDMMYYYY(dateStr);
  if (!p) return null;
  return new Date(p.year, p.month - 1, p.day).getTime();
}

function formatDateDE(dateStr?: string): string {
  const parsed = parseDDMMYYYY(dateStr);
  if (!parsed) return '—';
  return `${String(parsed.day).padStart(2, '0')}.${String(parsed.month).padStart(2, '0')}.${parsed.year}`;
}

/** Founding-type date (first entry, founding document): the bare year when
 *  the register only knows the year (its 1 January placeholder), else null. */
function foundingYearOnly(dateStr?: string): string | null {
  const precise = foundingDateAtPrecision(dateStr);
  return precise && /^\d{4}$/.test(precise) ? precise : null;
}

/** TED publishes ISO dates, unlike the CD timeline's DD.MM.YYYY. */
export function formatDateISO(dateStr?: string | null): string {
  if (!dateStr) return '—';
  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) return dateStr;
  return parsed.toLocaleDateString('de-DE');
}

export function formatCurrency(amount: number, currency?: string): string {
  try {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency || 'EUR',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString('de-DE')} ${currency || 'EUR'}`;
  }
}

function memberDisplayName(member: CompanyHistoryMember): string {
  // Prefer new granular fields: [title] [first_name] [last_name]
  if (member.first_name || member.last_name) {
    const parts = [member.title, member.first_name, member.last_name].filter(
      Boolean,
    );
    if (parts.length > 0) return parts.join(' ');
  }
  // Fall back to full_name
  if (member.full_name) return member.full_name;
  // Fall back to legacy name/surname
  const surname = member.surname || member.last_name;
  const firstName = member.name || member.first_name || member.vorname;
  const titles =
    Array.isArray(member.titles) && member.titles.length > 0
      ? member.titles.join(' ')
      : '';
  const namePart =
    surname && firstName
      ? `${surname}, ${firstName}`
      : surname || firstName || '';
  if (namePart && titles) return `${titles} ${namePart}`;
  if (namePart) return namePart;
  if (member.organization) return member.organization;
  return 'Unknown';
}

function calculateAge(dateStr: string): number | null {
  // Try DD.MM.YYYY
  const p = parseDDMMYYYY(dateStr);
  if (p) {
    const today = new Date();
    let age = today.getFullYear() - p.year;
    if (
      today.getMonth() + 1 < p.month ||
      (today.getMonth() + 1 === p.month && today.getDate() < p.day)
    )
      age--;
    return age >= 0 ? age : null;
  }
  // Try ISO
  try {
    const dob = new Date(dateStr);
    if (isNaN(dob.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    if (
      today.getMonth() < dob.getMonth() ||
      (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())
    )
      age--;
    return age >= 0 ? age : null;
  } catch {
    return null;
  }
}

// =============================================================================
// EVENT BUILDERS
// =============================================================================

export function buildCompanyEvents(
  history: CompanyHistory,
  t: TFunction,
): CompanyEvent[] {
  const events: CompanyEvent[] = [];

  const processList = (
    items: CompanyHistory['firma'],
    titleFirst: string,
    titleChange: string,
    icon: LucideIcon,
    iconBg: string,
    iconColor: string,
    getValue?: (data: RegisterFieldEventData) => string | undefined,
    getDetail?: (data: RegisterFieldEventData) => string | undefined,
    alwaysExpanded?: boolean,
  ) => {
    const sorted = [...items].sort(
      (a, b) =>
        (toTimestamp(a.entry_date ?? a.data.entry_date) ?? 0) -
        (toTimestamp(b.entry_date ?? b.data.entry_date) ?? 0),
    );
    for (let i = 0; i < sorted.length; i++) {
      const evt = sorted[i];
      const dateStr = evt.entry_date ?? evt.data.entry_date;
      const ts = toTimestamp(dateStr);
      if (ts == null) continue;
      events.push({
        timestamp: ts,
        date: formatDateDE(dateStr),
        icon,
        iconBg,
        iconColor,
        title: i === 0 ? titleFirst : titleChange,
        value: getValue?.(evt.data),
        detail: getDetail?.(evt.data),
        alwaysExpanded,
      });
    }
  };

  processList(
    history.firma,
    t('companyHistory.nameRegistered'),
    t('companyHistory.nameChange'),
    Building2,
    'bg-violet-100 dark:bg-violet-900/40',
    'text-violet-600 dark:text-violet-400',
    (d) => d.firma,
  );
  processList(
    history.sitz,
    t('companyHistory.seatRegistered'),
    t('companyHistory.seatChange'),
    Home,
    'bg-emerald-100 dark:bg-emerald-900/40',
    'text-emerald-600 dark:text-emerald-400',
    (d) => d.sitz,
  );
  processList(
    history.geschaeftsanschrift,
    t('companyHistory.addressRegistered'),
    t('companyHistory.addressChange'),
    MapPinHouse,
    'bg-teal-100 dark:bg-teal-900/40',
    'text-teal-600 dark:text-teal-400',
    (d) => d.geschaeftsanschrift,
  );
  processList(
    history.rechtsform,
    t('companyHistory.legalFormRegistered'),
    t('companyHistory.legalFormChange'),
    Scale,
    'bg-purple-100 dark:bg-purple-900/40',
    'text-purple-600 dark:text-purple-400',
    (d) => d.rechtsform,
    (d) =>
      d.previous_rechtsform
        ? `${d.previous_rechtsform} → ${d.rechtsform}`
        : undefined,
  );
  processList(
    history.gegenstand,
    t('companyHistory.purposeRegistered'),
    t('companyHistory.purposeChange'),
    ScrollText,
    'bg-orange-100 dark:bg-orange-900/40',
    'text-orange-600 dark:text-orange-400',
    undefined,
    (d) => d.gegenstand,
    true,
  );

  // Satzung / Gesellschaftsvertrag events
  // Skip the first satzung event (entry_number 1) when erste_eintragung already
  // shows the Gründungsdokument info, as they are redundant.
  const skipFirstSatzung =
    history.erste_eintragung?.data?.gruendungsdokument &&
    history.erste_eintragung?.data?.gruendungsdokument_datum;
  if (history.satzung_events?.length) {
    for (const evt of history.satzung_events) {
      if (skipFirstSatzung && evt.entry_number === 1) continue;
      const dateStr =
        evt.entry_date ?? evt.data.entry_date ?? evt.data.document_date;
      const ts = toTimestamp(dateStr);
      if (ts == null) continue;
      const isCreated = evt.data.event_type === 'created';
      const docType =
        evt.data.document_type || t('companyHistory.satzung', 'Satzung');
      events.push({
        timestamp: ts,
        date: formatDateDE(dateStr),
        icon: BookOpen,
        iconBg: 'bg-indigo-100 dark:bg-indigo-900/40',
        iconColor: 'text-indigo-600 dark:text-indigo-400',
        title: isCreated
          ? t('companyHistory.satzungCreated', '{{docType}} erstellt', {
              docType,
            })
          : t('companyHistory.satzungAmended', '{{docType}} geändert', {
              docType,
            }),
        value: docType,
      });
    }
  }

  // Erste Eintragung (first registration)
  if (history.erste_eintragung) {
    const evt = history.erste_eintragung;
    const dateStr =
      evt.entry_date ?? evt.data.entry_date ?? evt.data.erste_eintragung_datum;
    const ts = toTimestamp(dateStr);
    if (ts != null) {
      const detail = evt.data.has_earlier_entries
        ? t(
            'companyHistory.migratedFromOtherRegister',
            'Umschreibung aus vorherigem Registerblatt',
          )
        : undefined;
      const docYearOnly = foundingYearOnly(evt.data.gruendungsdokument_datum);
      const docInfo =
        evt.data.gruendungsdokument && evt.data.gruendungsdokument_datum
          ? docYearOnly
            ? `${evt.data.gruendungsdokument} von ${docYearOnly}`
            : `${evt.data.gruendungsdokument} vom ${evt.data.gruendungsdokument_datum}`
          : evt.data.gruendungsdokument || undefined;
      events.push({
        timestamp: ts,
        date: foundingYearOnly(dateStr) ?? formatDateDE(dateStr),
        icon: Bookmark,
        iconBg: 'bg-sky-100 dark:bg-sky-900/40',
        iconColor: 'text-sky-600 dark:text-sky-400',
        title: t('companyHistory.firstRegistration', 'Erste Eintragung'),
        value: docInfo,
        detail,
      });
    }
  }

  return events.sort((a, b) => b.timestamp - a.timestamp);
}

export function buildCapitalEvents(
  history: CompanyHistory,
  t: TFunction,
): CapitalEvent[] {
  const events: CapitalEvent[] = [];

  for (const evt of history.kapital) {
    const dateStr = evt.entry_date ?? evt.data.entry_date;
    const ts = toTimestamp(dateStr);
    if (ts == null) continue;
    const delta = evt.data.delta;
    const isIncrease = delta != null && delta > 0;
    const isDecrease = delta != null && delta < 0;

    events.push({
      timestamp: ts,
      date: formatDateDE(dateStr),
      icon: isIncrease ? TrendingUp : isDecrease ? TrendingDown : Euro,
      iconBg: isIncrease
        ? 'bg-green-100 dark:bg-green-900/40'
        : isDecrease
          ? 'bg-red-100 dark:bg-red-900/40'
          : 'bg-amber-100 dark:bg-amber-900/40',
      iconColor: isIncrease
        ? 'text-green-600 dark:text-green-400'
        : isDecrease
          ? 'text-red-600 dark:text-red-400'
          : 'text-amber-600 dark:text-amber-400',
      type: isIncrease
        ? t('companyHistory.capitalIncrease', 'Capital Increase')
        : isDecrease
          ? t('companyHistory.capitalDecrease', 'Capital Decrease')
          : t('companyHistory.capitalSet', 'Share Capital Set'),
      delta:
        delta != null && delta !== 0
          ? `${delta > 0 ? '+' : ''}${formatCurrency(delta, evt.data.currency)}`
          : undefined,
      deltaColor: isIncrease
        ? 'text-green-600 dark:text-green-400'
        : isDecrease
          ? 'text-red-600 dark:text-red-400'
          : undefined,
      total: formatCurrency(evt.data.amount, evt.data.currency),
    });
  }

  return events.sort((a, b) => b.timestamp - a.timestamp);
}

export function buildBoardEvents(history: CompanyHistory, t: TFunction): BoardEvent[] {
  const events: BoardEvent[] = [];

  for (const evt of history.board_members) {
    const dateStr = evt.entry_date ?? evt.data.entry_date;
    const ts = toTimestamp(dateStr);
    if (ts == null) continue;
    const member = evt.data.member || {};
    const action = evt.data.action || 'entered';
    const isJoined = action === 'entered';
    const isLeft = action === 'exited';
    const position = member.position || member.rolle || '';

    // Kommanditisten are folded into board_members by the CD aggregator
    // (position "Kommanditist"), but they are limited partners, not management
    // — keep them out of the Geschäftsführung timeline. A large family KG can
    // otherwise flood it with hundreds of Kommanditist join/leave events.
    if (/kommanditist/i.test(position)) continue;

    // Calculate age
    const birthStr = member.birthdate || member.date_of_birth;
    const age = birthStr ? calculateAge(birthStr) : null;

    events.push({
      timestamp: ts,
      date: formatDateDE(dateStr),
      icon: isJoined ? UserPlus : isLeft ? UserMinus : RefreshCw,
      iconBg: isJoined
        ? 'bg-green-100 dark:bg-green-900/40'
        : isLeft
          ? 'bg-red-100 dark:bg-red-900/40'
          : 'bg-slate-100 dark:bg-slate-900/40',
      iconColor: isJoined
        ? 'text-green-600 dark:text-green-400'
        : isLeft
          ? 'text-red-600 dark:text-red-400'
          : 'text-slate-600 dark:text-slate-400',
      name: memberDisplayName(member),
      role: position || undefined,
      age,
      status: isJoined
        ? t('companyHistory.joined', 'Joined')
        : isLeft
          ? t('companyHistory.left', 'Left')
          : t('companyHistory.changed', 'Changed'),
      statusColor: isJoined
        ? 'text-green-600 dark:text-green-400'
        : isLeft
          ? 'text-red-600 dark:text-red-400'
          : 'text-muted-foreground',
    });
  }

  return events.sort((a, b) => b.timestamp - a.timestamp);
}

export function buildInsolvencyEvents(
  records: InsolvencyRecordItem[],
): InsolvencyEvent[] {
  const events: InsolvencyEvent[] = [];
  for (const r of records) {
    // Undated events are kept rather than dropped: a plain "Die Gesellschaft
    // ist aufgelöst." states no date, and it is exactly the entry that puts a
    // company into liquidation. Sorting them last beats hiding them.
    const ts = toTimestamp(r.publish_date);
    events.push({
      timestamp: ts ?? 0,
      date: ts == null ? '—' : formatDateDE(r.publish_date),
      category: r.category,
      aktenzeichen: r.aktenzeichen || '—',
      court: r.court || '—',
      dissolved: r.dissolved === true,
    });
  }
  return events.sort((a, b) => b.timestamp - a.timestamp);
}
